import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { API_BASE, formatApiError } from "./AuthContext";
import { X, ShieldCheck, Loader2, Ban, AlertTriangle } from "lucide-react";

function useMediaUrl(sessionId, type) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let obj = null;
    axios.get(`${API_BASE}/voi/sessions/${sessionId}/media/${type}`, { responseType: "blob" })
      .then(({ data }) => { obj = URL.createObjectURL(data); setUrl(obj); })
      .catch(() => setErr(true));
    return () => obj && URL.revokeObjectURL(obj);
  }, [sessionId, type]);
  return { url, err };
}

function Check({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-start gap-2 text-[13px] text-[var(--ink)] cursor-pointer">
      <input type="checkbox" className="mt-0.5" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      <span>{label}</span>
    </label>
  );
}

function Evidence({ session, client }) {
  const img = useMediaUrl(session.session_id, "ID_PHOTO");
  const vid = useMediaUrl(session.session_id, "LIVENESS_VIDEO");
  return (
    <div className="space-y-3" onContextMenu={(e) => e.preventDefault()}>
      <div className="overline">ID photo · {(session.id_type || "").replace("_", " ").toLowerCase()}</div>
      <div className="bg-[var(--subtle)] rounded-md min-h-[160px] flex items-center justify-center overflow-hidden">
        {img.url ? <img src={img.url} alt="ID" className="max-h-72 object-contain" data-testid="voi-review-id-image" />
          : img.err ? <span className="text-[12px] text-[var(--accent)]">Unavailable</span> : <Loader2 className="animate-spin" size={16} />}
      </div>
      <div className="overline">Liveness video · phrase “<span className="text-[var(--ink)]" data-testid="voi-review-phrase">{session.dynamic_phrase}</span>”</div>
      <div className="bg-black rounded-md min-h-[160px] flex items-center justify-center overflow-hidden">
        {vid.url ? <video src={vid.url} controls controlsList="nodownload" playsInline className="max-h-80" data-testid="voi-review-video" />
          : vid.err ? <span className="text-[12px] text-[var(--accent)]">Unavailable</span> : <Loader2 className="animate-spin text-white" size={16} />}
      </div>
      <div className="text-[12px] text-[var(--ink-2)] card p-3" data-testid="voi-review-crm-details">
        <div className="overline mb-1">CRM record</div>
        <div>{client?.name}</div>
        <div className="text-[var(--ink-3)]">{[client?.primary_contact_name, client?.primary_contact_email].filter(Boolean).join(" · ")}</div>
      </div>
    </div>
  );
}

export default function VoiReviewDialog({ session, client, config, onClose, onDone }) {
  const [f, setF] = useState({ id_type_sighted: session.id_type || "", id_country_state: "", visual_match_confirmed: false,
    phrase_confirmed: false, details_match_confirmed: false, ato_checked_in_osa: false });
  const [points, setPoints] = useState([]);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [sendNew, setSendNew] = useState(true);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const togglePoint = (p) => setPoints((xs) => (xs.includes(p) ? xs.filter((x) => x !== p) : [...xs, p]));
  const hasTan = !!config?.my_tax_agent_number;
  const ready = hasTan && f.id_type_sighted && f.id_country_state && f.visual_match_confirmed && f.phrase_confirmed
    && f.details_match_confirmed && f.ato_checked_in_osa && points.length >= 2;

  const act = async (path, body, msg) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/voi/sessions/${session.session_id}/${path}`, body);
      const failed = Object.entries(data.retry_session?.delivery || {}).filter(([, r]) => !r.ok);
      if (failed.length) toast.warning(`Rejected — new link created, but ${failed.map(([ch, r]) => `${ch}: ${r.error}`).join("; ")}. Copy the link from the ID check tab.`);
      else toast.success(msg);
      onDone();
    }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="voi-review-dialog">
      <div className="bg-[var(--paper)] rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)] sticky top-0 bg-[var(--paper)] z-10">
          <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-[var(--moss)]" />
            <span className="font-medium text-[14px]">Review identity · {client?.name}</span></div>
          <button onClick={onClose} data-testid="voi-review-close"><X size={18} /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-6 p-5">
          <Evidence session={session} client={client} />
          <div className="space-y-5">
            <section className="space-y-2">
              <div className="overline">1 · Visual check</div>
              <div className="grid grid-cols-2 gap-2">
                <select value={f.id_type_sighted} onChange={(e) => set("id_type_sighted")(e.target.value)} data-testid="voi-review-id-type"
                  className="border border-[var(--border)] rounded-md px-2 py-1.5 text-[13px] bg-white">
                  <option value="">ID type…</option>
                  {config?.id_types.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                </select>
                <select value={f.id_country_state} onChange={(e) => set("id_country_state")(e.target.value)} data-testid="voi-review-id-region"
                  className="border border-[var(--border)] rounded-md px-2 py-1.5 text-[13px] bg-white">
                  <option value="">Issued in…</option>
                  {config?.id_regions.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <Check checked={f.visual_match_confirmed} onChange={set("visual_match_confirmed")} label="Face in the video matches the ID photo" testid="voi-check-face" />
              <Check checked={f.phrase_confirmed} onChange={set("phrase_confirmed")} label={`Client clearly said “${session.dynamic_phrase}”`} testid="voi-check-phrase" />
              <Check checked={f.details_match_confirmed} onChange={set("details_match_confirmed")} label="Name and details on the ID match the CRM record" testid="voi-check-details" />
            </section>
            <section className="space-y-2">
              <div className="overline">2 · Source check — ATO Online Services for Agents</div>
              <p className="text-[12px] text-[var(--ink-3)]">Tick at least two data points you confirmed against the ATO record. Only the labels are stored — never the values.</p>
              <div className="flex flex-wrap gap-1.5">
                {config?.ato_data_points.map((p, i) => (
                  <button key={p} type="button" onClick={() => togglePoint(p)} data-testid={`voi-ato-point-${i}`}
                    className={`text-[12px] px-2.5 py-1 rounded-full border transition-colors ${points.includes(p) ? "bg-[var(--ink)] hover:bg-[var(--ink)] text-white hover:text-white border-[var(--ink)]" : "border-[var(--border)] hover:border-[var(--ink)]"}`}>{p}</button>
                ))}
              </div>
              <div className="text-[11px] text-[var(--ink-3)]" data-testid="voi-ato-count">{points.length} of 2 required selected</div>
              <Check checked={f.ato_checked_in_osa} onChange={set("ato_checked_in_osa")} label="I checked these in Online Services for Agents just now" testid="voi-check-osa" />
            </section>
            {!hasTan && (
              <div className="text-[12px] p-3 rounded-md bg-[var(--accent-light,#FEF2F2)] text-[var(--accent)] flex gap-2" data-testid="voi-no-tan-warning">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>Your tax agent number is needed on the audit record. <Link to="/settings/profile" className="underline">Add it in My profile</Link>.</span>
              </div>
            )}
            {rejecting ? (
              <div className="space-y-2">
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} data-testid="voi-reject-reason"
                  placeholder="Reason (e.g. face not visible, phrase not spoken). Never include a TFN."
                  className="w-full border border-[var(--border)] rounded-md px-3 py-2 text-[13px]" />
                <Check checked={sendNew} onChange={setSendNew} testid="voi-reject-send-new"
                  label={`Send ${client?.primary_contact_name?.split(" ")[0] || "the client"} a new link by email with this reason`} />
                <div className="flex gap-2">
                  <button className="btn-ghost" onClick={() => setRejecting(false)} data-testid="voi-reject-cancel">Cancel</button>
                  <button className="btn-secondary flex-1 justify-center" disabled={busy || reason.trim().length < 3} data-testid="voi-reject-confirm"
                    onClick={() => act("reject", { reason, send_new_link: sendNew, channels: ["email", ...(config?.sms_configured ? ["sms"] : [])] },
                      sendNew ? "Rejected — new link sent to client" : "Rejected — media destroyed")}>Reject & purge media</button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 pt-2 border-t border-[var(--border)]">
                <button className="btn-ghost" onClick={() => setRejecting(true)} data-testid="voi-reject-open"><Ban size={14} /> Reject</button>
                <button className="btn-primary flex-1 justify-center" disabled={!ready || busy} data-testid="voi-approve"
                  onClick={() => act("approve", { ...f, ato_data_points: points }, "Verified — media destroyed")}>
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Verify & purge media
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
